import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  SpaceBetween,
  Button,
  FormField,
  Input,
  Box,
  Header,
  Toggle,
  ColumnLayout,
} from '@cloudscape-design/components';
import { QuestionFormParams } from './QuestionFormWizard';
import { FileData } from '../textEditor/TextEditor';
import TextEditor from '../textEditor/TextEditor';
import { SubQuestion } from 'src/types/helios/Question';
import { MARKDOWN_TEXT_WARNING } from 'src/contstant/commonLabels';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { FileMetadata } from '../FileDisplay';
import { getSubQuestionFieldKey } from 'src/helper/questionHelper';

interface SubQuestionSidePanelProps {
  mode: 'add' | 'edit';
  editingIndex?: number;
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  fileList: Record<string, FileData[]>;
  setFileList: React.Dispatch<React.SetStateAction<Record<string, FileData[]>>>;
  documentFiles?: Record<string, File[]>;
  setDocumentFiles?: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
  documentFilesMetadata?: Record<string, FileMetadata[]>;
  setDocumentFilesMetadata?: React.Dispatch<
    React.SetStateAction<Record<string, FileMetadata[]>>
  >;
  onClose: () => void;
}

const SubQuestionSidePanel: React.FC<SubQuestionSidePanelProps> = ({
  mode,
  editingIndex,
  formData,
  setFormData,
  fileList,
  setFileList,
  documentFiles,
  setDocumentFiles,
  documentFilesMetadata,
  setDocumentFilesMetadata,
  onClose,
}) => {
  // Form state for side panel
  const [panelFormData, setPanelFormData] = useState<{
    title: string;
    instruction: string;
    taskName: string;
    taskDescription: string;
    taskDurationDays: number;
    hasTask: boolean;
    requireCommentOnFail: boolean;
    requireEvidenceOnFail: boolean;
    requireCommentOnPass: boolean;
    requireEvidenceOnPass: boolean;
  }>({
    title: '',
    instruction: '',
    taskName: '',
    taskDescription: '',
    taskDurationDays: 1,
    hasTask: false,
    requireCommentOnFail: false,
    requireEvidenceOnFail: false,
    requireCommentOnPass: false,
    requireEvidenceOnPass: false,
  });

  const [subQuestionFiles, setSubQuestionFiles] = useState<Record<string, FileData[]>>(
    {},
  );

  // Separate document states for instruction and taskDescription
  const [instructionDocumentFiles, setInstructionDocumentFiles] = useState<File[]>([]);
  const [instructionDocumentMetadata, setInstructionDocumentMetadata] = useState<
    FileMetadata[]
  >([]);
  const [taskDescriptionDocumentFiles, setTaskDescriptionDocumentFiles] = useState<
    File[]
  >([]);
  const [taskDescriptionDocumentMetadata, setTaskDescriptionDocumentMetadata] = useState<
    FileMetadata[]
  >([]);

  // Track if we've initialized to prevent re-initialization
  const initializedRef = useRef<string | null>(null);

  // Memoize metadata arrays to prevent infinite loops in TextEditor
  const memoizedInstructionMetadata = useMemo(
    () => instructionDocumentMetadata,
    [instructionDocumentMetadata],
  );
  const memoizedTaskDescriptionMetadata = useMemo(
    () => taskDescriptionDocumentMetadata,
    [taskDescriptionDocumentMetadata],
  );

  // Initialize form data when component mounts or editing index changes
  useEffect(() => {
    const initKey = `${mode}_${editingIndex}`;

    // Only initialize once per mode/editingIndex combination
    if (initializedRef.current === initKey) {
      return;
    }

    initializedRef.current = initKey;

    if (
      mode === 'edit' &&
      editingIndex !== undefined &&
      formData.subQuestions &&
      formData.subQuestions[editingIndex]
    ) {
      const subQuestion = formData.subQuestions[editingIndex];
      setPanelFormData({
        title: subQuestion.title || '',
        instruction: subQuestion.instruction || '',
        taskName: subQuestion.taskDetails?.taskName || '',
        taskDescription: subQuestion.taskDetails?.taskDescription || '',
        taskDurationDays: subQuestion.taskDetails?.taskDurationDays || 1,
        hasTask: !!subQuestion.taskDetails,
        requireCommentOnFail: subQuestion.requireCommentOnFail ?? false,
        requireEvidenceOnFail: subQuestion.requireEvidenceOnFail ?? false,
        requireCommentOnPass: subQuestion.requireCommentOnPass ?? false,
        requireEvidenceOnPass: subQuestion.requireEvidenceOnPass ?? false,
      });

      // Load existing documents for instruction field
      const instructionFieldKey = getSubQuestionFieldKey(editingIndex, 'instruction');
      const instructionMetadata = documentFilesMetadata?.[instructionFieldKey] || [];
      const instructionFiles = documentFiles?.[instructionFieldKey] || [];

      // If we have metadata but no files, create placeholder File objects
      if (instructionMetadata.length > 0 && instructionFiles.length === 0) {
        const placeholderFiles = instructionMetadata.map((metadata) => {
          // Create a placeholder File object for existing documents
          return new File([], metadata.fileName, { type: 'application/octet-stream' });
        });
        setInstructionDocumentFiles(placeholderFiles);
      } else {
        setInstructionDocumentFiles(instructionFiles);
      }
      setInstructionDocumentMetadata(instructionMetadata);

      // Load existing documents for taskDescription field
      const taskDescriptionFieldKey = getSubQuestionFieldKey(
        editingIndex,
        'taskDescription',
      );
      const taskDescriptionMetadata =
        documentFilesMetadata?.[taskDescriptionFieldKey] || [];
      const taskDescriptionFiles = documentFiles?.[taskDescriptionFieldKey] || [];

      // If we have metadata but no files, create placeholder File objects
      if (taskDescriptionMetadata.length > 0 && taskDescriptionFiles.length === 0) {
        const placeholderFiles = taskDescriptionMetadata.map((metadata) => {
          // Create a placeholder File object for existing documents
          return new File([], metadata.fileName, { type: 'application/octet-stream' });
        });
        setTaskDescriptionDocumentFiles(placeholderFiles);
      } else {
        setTaskDescriptionDocumentFiles(taskDescriptionFiles);
      }
      setTaskDescriptionDocumentMetadata(taskDescriptionMetadata);
    } else {
      // Reset for add mode
      setPanelFormData({
        title: '',
        instruction: '',
        taskName: '',
        taskDescription: '',
        taskDurationDays: 1,
        hasTask: false,
        requireCommentOnFail: false,
        requireEvidenceOnFail: false,
        requireCommentOnPass: false,
        requireEvidenceOnPass: false,
      });
      setInstructionDocumentFiles([]);
      setInstructionDocumentMetadata([]);
      setTaskDescriptionDocumentFiles([]);
      setTaskDescriptionDocumentMetadata([]);
    }
  }, [mode, editingIndex]);

  // Validation function
  const isFormValid = useCallback(() => {
    // Check mandatory fields
    if (!panelFormData.title.trim() || !panelFormData.instruction.trim()) {
      return false;
    }

    // In isFormValid function, add this check inside the hasTask condition
    if (panelFormData.hasTask) {
      if (
        !panelFormData.taskName.trim() ||
        !panelFormData.taskDescription.trim() ||
        panelFormData.taskDurationDays <= 0
      ) {
        return false;
      }
    }

    return true;
  }, [panelFormData]);

  // Handle saving sub-question
  const handleSave = useCallback(() => {
    // Validate form before saving
    if (!isFormValid()) {
      return;
    }

    setFileList((prev) => ({ ...prev, ...subQuestionFiles }));

    // Persist document files to parent state
    if (setDocumentFiles && setDocumentFilesMetadata) {
      const subQuestionIndex =
        editingIndex !== undefined ? editingIndex : formData.subQuestions?.length || 0;

      // Save instruction documents
      const instructionFieldKey = getSubQuestionFieldKey(subQuestionIndex, 'instruction');
      if (instructionDocumentFiles.length > 0 || instructionDocumentMetadata.length > 0) {
        setDocumentFiles((prev) => ({
          ...prev,
          [instructionFieldKey]: instructionDocumentFiles,
        }));
        setDocumentFilesMetadata((prev) => ({
          ...prev,
          [instructionFieldKey]: instructionDocumentMetadata,
        }));
      } else {
        // Clear if no documents
        setDocumentFiles((prev) => {
          const updated = { ...prev };
          delete updated[instructionFieldKey];
          return updated;
        });
        setDocumentFilesMetadata((prev) => {
          const updated = { ...prev };
          delete updated[instructionFieldKey];
          return updated;
        });
      }

      // Save taskDescription documents
      const taskDescriptionFieldKey = getSubQuestionFieldKey(
        subQuestionIndex,
        'taskDescription',
      );
      if (
        taskDescriptionDocumentFiles.length > 0 ||
        taskDescriptionDocumentMetadata.length > 0
      ) {
        setDocumentFiles((prev) => ({
          ...prev,
          [taskDescriptionFieldKey]: taskDescriptionDocumentFiles,
        }));
        setDocumentFilesMetadata((prev) => ({
          ...prev,
          [taskDescriptionFieldKey]: taskDescriptionDocumentMetadata,
        }));
      } else {
        // Clear if no documents
        setDocumentFiles((prev) => {
          const updated = { ...prev };
          delete updated[taskDescriptionFieldKey];
          return updated;
        });
        setDocumentFilesMetadata((prev) => {
          const updated = { ...prev };
          delete updated[taskDescriptionFieldKey];
          return updated;
        });
      }
    }

    const newSubQuestion: SubQuestion = {
      title: panelFormData.title,
      instruction: panelFormData.instruction,
      orderIndex:
        editingIndex !== undefined
          ? formData.subQuestions?.[editingIndex]?.orderIndex || 0
          : (formData.subQuestions?.length || 0) + 1,
      taskDetails: panelFormData.hasTask
        ? {
            taskName: panelFormData.taskName,
            taskDescription: panelFormData.taskDescription,
            taskDurationDays: panelFormData.taskDurationDays,
          }
        : undefined,
      requireCommentOnFail: panelFormData.requireCommentOnFail,
      requireEvidenceOnFail: panelFormData.requireEvidenceOnFail,
      requireCommentOnPass: panelFormData.requireCommentOnPass,
      requireEvidenceOnPass: panelFormData.requireEvidenceOnPass,
    };

    if (editingIndex !== undefined) {
      // Editing existing sub-question
      const updatedSubQuestions = [...(formData.subQuestions || [])];
      updatedSubQuestions[editingIndex] = {
        ...updatedSubQuestions[editingIndex],
        ...newSubQuestion,
      };
      setFormData((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
    } else {
      // Adding new sub-question
      const updatedSubQuestions = [...(formData.subQuestions || []), newSubQuestion];
      setFormData((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
    }

    // Close panel
    onClose();
  }, [
    panelFormData,
    editingIndex,
    formData.subQuestions,
    setFormData,
    onClose,
    isFormValid,
    subQuestionFiles,
    setFileList,
    instructionDocumentFiles,
    instructionDocumentMetadata,
    taskDescriptionDocumentFiles,
    taskDescriptionDocumentMetadata,
    setDocumentFiles,
    setDocumentFilesMetadata,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle adding task
  const handleAddTask = useCallback(() => {
    setPanelFormData((prev) => ({
      ...prev,
      hasTask: true,
      taskName: '',
      taskDescription: '',
      taskDurationDays: 1,
    }));
  }, []);

  // Handle removing task
  const handleRemoveTask = useCallback(() => {
    setPanelFormData((prev) => ({
      ...prev,
      hasTask: false,
      taskName: '',
      taskDescription: '',
      taskDurationDays: 0,
    }));
  }, []);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Scrollable content area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 0 16px 0' }}>
        <SpaceBetween size="m">
          <FormField
            data-testid="sub-question-title-field"
            label={<RequiredFieldLabel label="Sub-question Title" />}
            stretch
          >
            <Input
              data-testid="sub-question-title-input"
              value={panelFormData.title}
              onChange={({ detail }) =>
                setPanelFormData((prev) => ({ ...prev, title: detail.value }))
              }
              placeholder="Enter sub-question title"
            />
          </FormField>

          <FormField
            data-testid="sub-question-instruction-field"
            label={<RequiredFieldLabel label="Sub-question Instruction" />}
            stretch
            description={MARKDOWN_TEXT_WARNING}
          >
            <TextEditor
              data-testid="sub-question-instruction-editor"
              placeholder="Enter sub-question instruction..."
              value={panelFormData.instruction}
              onChange={(value) =>
                setPanelFormData((prev) => ({ ...prev, instruction: value }))
              }
              onChangeMedia={(data) =>
                setSubQuestionFiles((prev) => ({ ...prev, subquestionInstruction: data }))
              }
              showFileUpload={!!setDocumentFiles}
              initialFiles={instructionDocumentFiles}
              initialFileMetadata={memoizedInstructionMetadata}
              onFilesChange={(data) => {
                setInstructionDocumentFiles(data.files);
                setInstructionDocumentMetadata(data.metadata);
              }}
            />
          </FormField>

          <ColumnLayout data-testid="sub-question-fail-requirements" columns={2}>
            <FormField
              data-testid="sq-require-comment-on-fail-field"
              label="Comment Required on Fail"
            >
              <Toggle
                data-testid="sq-require-comment-on-fail-toggle"
                checked={panelFormData.requireCommentOnFail}
                onChange={({ detail }) =>
                  setPanelFormData((prev) => ({
                    ...prev,
                    requireCommentOnFail: detail.checked,
                  }))
                }
              />
            </FormField>
            <FormField
              data-testid="sq-require-evidence-on-fail-field"
              label="Attachment Required on Fail"
            >
              <Toggle
                data-testid="sq-require-evidence-on-fail-toggle"
                checked={panelFormData.requireEvidenceOnFail}
                onChange={({ detail }) =>
                  setPanelFormData((prev) => ({
                    ...prev,
                    requireEvidenceOnFail: detail.checked,
                  }))
                }
              />
            </FormField>
          </ColumnLayout>

          <ColumnLayout data-testid="sub-question-pass-requirements" columns={2}>
            <FormField
              data-testid="sq-require-comment-on-pass-field"
              label="Comment Required on Pass"
            >
              <Toggle
                data-testid="sq-require-comment-on-pass-toggle"
                checked={panelFormData.requireCommentOnPass}
                onChange={({ detail }) =>
                  setPanelFormData((prev) => ({
                    ...prev,
                    requireCommentOnPass: detail.checked,
                  }))
                }
              />
            </FormField>
            <FormField
              data-testid="sq-require-evidence-on-pass-field"
              label="Attachment Required on Pass"
            >
              <Toggle
                data-testid="sq-require-evidence-on-pass-toggle"
                checked={panelFormData.requireEvidenceOnPass}
                onChange={({ detail }) =>
                  setPanelFormData((prev) => ({
                    ...prev,
                    requireEvidenceOnPass: detail.checked,
                  }))
                }
              />
            </FormField>
          </ColumnLayout>

          {panelFormData.hasTask ? (
            <SpaceBetween size="m">
              <Header
                data-testid="task-details-header"
                variant="h3"
                actions={
                  <Button
                    data-testid="remove-task-button"
                    iconName="remove"
                    variant="link"
                    onClick={handleRemoveTask}
                  >
                    Remove Task
                  </Button>
                }
              >
                Task Details
              </Header>

              <FormField
                data-testid="task-name-field"
                label={<RequiredFieldLabel label="Task Name" />}
                stretch
              >
                <Input
                  data-testid="task-name-input"
                  value={panelFormData.taskName}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({ ...prev, taskName: detail.value }))
                  }
                  placeholder="Enter task name"
                />
              </FormField>

              <FormField
                data-testid="task-description-field"
                label={<RequiredFieldLabel label="Task Description" />}
                stretch
                description={MARKDOWN_TEXT_WARNING}
              >
                <TextEditor
                  data-testid="task-description-editor"
                  placeholder="Enter task description..."
                  value={panelFormData.taskDescription}
                  onChange={(value) =>
                    setPanelFormData((prev) => ({ ...prev, taskDescription: value }))
                  }
                  onChangeMedia={(data) =>
                    setSubQuestionFiles((prev) => ({ ...prev, subTaskDescription: data }))
                  }
                  showFileUpload={!!setDocumentFiles}
                  initialFiles={taskDescriptionDocumentFiles}
                  initialFileMetadata={memoizedTaskDescriptionMetadata}
                  onFilesChange={(data) => {
                    setTaskDescriptionDocumentFiles(data.files);
                    setTaskDescriptionDocumentMetadata(data.metadata);
                  }}
                />
              </FormField>

              <FormField
                data-testid="task-duration-field"
                label="Task Duration (Days)"
                stretch
              >
                <Input
                  data-testid="task-duration-input"
                  type="number"
                  value={panelFormData.taskDurationDays.toString()}
                  onChange={({ detail }) =>
                    setPanelFormData((prev) => ({
                      ...prev,
                      taskDurationDays: parseInt(detail.value) || 0,
                    }))
                  }
                  placeholder="0"
                />
              </FormField>
            </SpaceBetween>
          ) : (
            <Box data-testid="add-task-container" textAlign="right">
              <Button
                data-testid="add-task-button"
                iconName="add-plus"
                onClick={handleAddTask}
              >
                Add Task
              </Button>
            </Box>
          )}
        </SpaceBetween>
      </div>

      {/* Sticky footer with buttons */}
      <div
        style={{
          borderTop: '1px solid #e9ebed',
          padding: '16px 0 0 0',
          backgroundColor: 'white',
        }}
      >
        <Box data-testid="action-buttons-container" float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button data-testid="cancel-button" variant="link" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              data-testid="save-button"
              variant="primary"
              onClick={handleSave}
              disabled={!isFormValid()}
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      </div>
    </div>
  );
};

export default SubQuestionSidePanel;
